import React, { Component } from "react"
import PropTypes from "prop-types"
import { OnboardingHeader } from "components/OnboardingHeader"
import { Input, Button, Loader } from "@drifthq/tide-core"
import { getApiKeyForOrg } from "modules/apiKey/actions"
import { apiKey } from "modules/apiKey/selectors"
import { connect } from "react-redux"
import { bindActionCreators } from "redux"
import {
  updateOrgProperties,
  fetchOrgPropertiesSuccess,
  fetchOrgPropertiesFailed
} from "modules/org/actions"
import { getOrgProperties, getOrgId, getEmbedId } from "modules/org/selectors"
import config from "config"
import { pathOr, isEmpty, equals } from "ramda"
import { popUp } from "./helpers"
import { api } from "modules/org/api"
import SegmentLogo from "./segment-logo-with-title.svg"
import TitleCard from "components/OnboardingTitleCard"
import InstallNotification from "components/OnboardingInstallNotification"
import BackButton from "components/OnboardingBackButton"
import { track } from 'utils/trackingUtils'

import "./style.css"

const SUCCESS_MESSAGE = "Segment was successfully connected!"
const ERROR_MESSAGE = "Uh oh! Something went wrong before segment could connect. Try again!"
const SEGMENT_REDIRECT_URI = `${config.CUSTOMER_WEB_URL}/accept-integration/segment`
const SEGMENT_ENABLE_BUTTON_SRC = "https://static.segment.com/enable-button/v1/index.js"

function makeSegmentButtonPieces({ apiKey, embedId }) {
  const dataSettings = JSON.stringify({ apiKey, embedId })
  return [
    <div
      key="unique-key-2"
      className="segment-enable-button"
      data-integration="drift"
      data-settings={dataSettings}
      data-redirect-url={SEGMENT_REDIRECT_URI}
    />
  ]
}

class Segment extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    path: PropTypes.string.isRequired,
    orgProperties: PropTypes.object.isRequired,
    onContinue: PropTypes.func.isRequired
  }

  state = {
    updating: false,
    confirmed: false,
    segmentEnabled: false,
    errorNotification: false,
    successNotification: false,
    showNextButton: false,
  }

  componentDidMount() {
    this.props.actions.getApiKeyForOrg()
    const segmentScript = document.createElement("script")
    segmentScript.src = SEGMENT_ENABLE_BUTTON_SRC
    document.head.appendChild(segmentScript)

    if (isEmpty(this.props.orgProperties)) {
      this.fetchOrgProps()
    }
  }

  fetchOrgProps = async () => {
    const { orgId } = this.props
    try {
      const orgData = await api.fetchOrgProperties(orgId)
      fetchOrgPropertiesSuccess(orgData)
    } catch (err) {
      fetchOrgPropertiesFailed(err)
    }
  }

  trackSegmentInteraction = ({ eventType, ctaInfo = {} }) => {
    const { path } = this.props
    track(`Onboarding Segment Step- ${eventType}`, { route: path, ...ctaInfo })
  }

  componentWillReceiveProps({ orgProperties }) {
    const { segmentEnabled } = this.props.orgProperties
    if (!equals(segmentEnabled, orgProperties.segmentEnabled)) {
      this.setState({ updating: false })
      if (orgProperties.segmentEnabled) {
        this.trackSegmentInteraction({ eventType: 'Success' })
        this.setState({ successNotification: true, showNextButton: true })
      }
    }
  }

  handlePopUp = () => {
    const link = document.querySelector(".segment-enable-button a").href
    popUp({
      path: link,
      onClose: () => {
        if (this.state.updating) {
          this.trackSegmentInteraction({ eventType: 'Error', ctaInfo: { CTA: 'closed popup before connection completed' } })
          this.setState({ updating: false, errorNotification: true })
        }
      },
      onMessage: () => {
        this.props.actions.updateOrgProperties({
          properties: { segmentEnabled: true },
          redirect: false
        })
        this.setState({
          updating: false,
          segmentEnabled: true
        })
      }
    })
  }

  handleSubmit = () => {
    this.trackSegmentInteraction({ eventType: 'Clicked', ctaInfo: { CTA: 'connect Segment to Drift and render popup', text: 'Connect', type: 'button' } })
    this.handlePopUp()
    this.setState({ updating: true })
  }

  renderSegmentButton() {
    const apiKey = pathOr("", ["apiKey", "accessToken"], this.props)
    const embedId = pathOr("", ["embed", "id"], this.state)
    const nodes = makeSegmentButtonPieces({ apiKey, embedId })
    const { updating, showNextButton } = this.state
    const disabled = updating || !this.state.confirmed
    const buttonClass = "segment-install-button"
    if (showNextButton) {
      return (
        <Button
          className={buttonClass}
          onClick={this.props.onContinue}
          size="large"
        >
          Next
        </Button>
      )
    }
    const buttonOrLoader = updating ? <Loader /> :
      (nodes.concat(<Button //TODO: clean this up
        key="unique-key"
        className={buttonClass}
        onClick={this.handleSubmit}
        disabled={disabled}
        size="large"
      >
        Connect
      </Button>)
      )
    return buttonOrLoader
  }

  renderCardTitle = () => (
    <img src={SegmentLogo} className="segment-install-logo-title" alt="segment-logo" />
  )

  render() {
    const { errorNotification, successNotification } = this.state

    return (
      <div className="segment-install-wrapper">
        <OnboardingHeader
          title="Install Drift with Segment"
          description="Don’t worry — installing Drift won’t make any changes to your website now. This step should only take a few minutes, and put you on the fast track to getting value!"
        />
        <TitleCard title={this.renderCardTitle()} className="segment-install-card">
          <div className="segment-install-title-card-label">
            1-Click Install
          </div>
          <div className="segment-install-title-card-copy">
            If you’re a Segment customer, you know how easy it is to get new
            integrations up and running. Drift + Segment works the same way --
            one click and no need to touch any code at all.
            <br />
            <br />
            Click the button below to connect your account and get Drift
            Messenger live on your site.
          </div>
          <div className="segment-install-checkbox">
            <Input
              type="checkbox"
              checked={this.state.confirmed}
              onChange={() => {
                this.setState({ confirmed: !this.state.confirmed })
              }}
              id="confirm-set-up"
            />
            <div className="segment-install-checkbox-label">
              I’ve already set up Segment for my website or app.
            </div>
          </div>
        </TitleCard>
        <InstallNotification
          errorText={ERROR_MESSAGE}
          successText={SUCCESS_MESSAGE}
          isError={errorNotification}
          isSuccess={successNotification}
        />
        {this.renderSegmentButton()}
        <BackButton/>
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      getApiKeyForOrg,
      updateOrgProperties
    },
    dispatch
  )
})

const mapStateToProps = state => {
  return {
    apiKey: apiKey(state),
    orgId: getOrgId(state),
    embedId: getEmbedId(state),
    orgProperties: getOrgProperties(state),
    segmentEnabled: state.segmentEnabled
  }
}

const SegmentInstall = connect(
  mapStateToProps,
  mapDispatchToProps
)(Segment)

export default SegmentInstall



// WEBPACK FOOTER //
// ./src/layouts/OnboardingInstallStep/SegmentInstall/index.js